<template>
  <div class="health-record-list min-h-screen theme-bg">
    <!-- 固定在顶部的导航和查询条件 -->
    <div class="fixed-header">
      <!-- 顶部导航 -->
      <van-nav-bar 
        title="健康记录" 
        left-arrow 
        @click-left="$router.back()"
        class="theme-header"
        :border="false"
      >
      </van-nav-bar>
      
      <!-- 我的宠物伙伴模块 -->
      <div class="pet-partners-container">
        <div class="pet-partners-scroll">
          <div 
            v-for="(pet, index) in devices" 
            :key="index" 
            class="pet-partner-item" 
            :class="{'pet-partner-active': machineId === pet.machineId}"
            @click="selectPet(pet.machineId)"
          >
            <div class="pet-avatar-container">
              <img :src="pet.petImage" alt="宠物照片" class="pet-avatar" />
              <div class="pet-status" :class="{'status-online': pet.machineStatus == 'online'}"></div>
            </div>
            <p class="pet-name">{{ pet.petName }}</p>
          </div>
        </div>
      </div>
      
      <!-- 简单查询条件 -->
      <div class="query-container">
        <!-- 筛选标签 -->
        <div class="filter-tabs">
          <div 
            v-for="(option, index) in recordTypeOptions" 
            :key="index"
            class="filter-tab"
            :class="{'filter-tab-active': filters.recordType === option.value}"
            @click="selectFilterTab(option.value)"
          >
            <font-awesome-icon 
              v-if="option.value !== 'all'" 
              :icon="getRecordTypeIcon(option.value)" 
              class="filter-tab-icon"
            />
            <font-awesome-icon 
              v-else
              icon="th-list" 
              class="filter-tab-icon"
            />
            {{ option.text }}
          </div>
        </div>
        
        <!-- 日期筛选 -->
        <div class="date-filter">
          <div class="date-filter-input" @click="showSingleDatePicker = true">
            <font-awesome-icon icon="calendar-alt" class="date-filter-icon" />
            <span v-if="filters.singleDate" class="date-value">{{ filters.singleDate }}</span>
            <span v-else class="date-placeholder">选择日期筛选</span>
          </div>
          <button v-if="filters.singleDate" class="date-clear-btn" @click="clearDateFilter">
            <font-awesome-icon icon="times-circle" />
          </button>
        </div>
      </div>
    </div>
    
    <!-- 主要内容区域 -->
    <div class="content-container">
      <!-- 记录统计 -->
      <!-- <div v-if="!loading && records.length > 0" class="records-summary">
        共 <span class="font-semibold text-blue-500">{{ totalRecords }}</span> 条记录
      </div> -->
      <!-- 顶部间距占位 -->
      <div class="top-spacer"></div>
      
      <!-- 额外间距，确保卡片不被筛选栏遮挡 -->
      <div class="extra-spacer"></div>
      
      <!-- 记录列表 -->
      <div class="mb-4">
        <div v-if="loading" class="py-16 text-center">
          <div class="top-spacer-empty"></div>
          <div class="extra-spacer"></div>
          <van-loading type="spinner" color="#1989fa" size="24px" />
          <p class="text-gray-500 mt-4">加载中...</p>
        </div>
        
        <div v-else-if="records.length === 0" class="py-16 text-center">
          <div class="empty-state">
            <div class="top-spacer-empty"></div>
            <div class="extra-spacer"></div>
            <font-awesome-icon icon="clipboard" class="text-5xl text-gray-200 mb-4" />
            <p class="text-gray-500 mb-6">暂无健康记录</p>
            <van-button plain round type="primary" size="small" @click="goToAddRecord">添加记录</van-button>
          </div>
        </div>
        
        <div v-else>
          <!-- 记录卡片列表 -->
          <div v-for="(record, index) in records" :key="index" class="record-card theme-card">
            <!-- 记录标题栏 -->
            <div class="record-header" :class="getRecordTypeClass(record.healthRecordType)">
              <div class="flex items-center">
                <div class="record-icon-container">
                  <font-awesome-icon :icon="getRecordTypeIcon(record.healthRecordType)" class="text-white" />
                </div>
                <div>
                  <h3 class="record-title">{{ record.healthRecordName }}</h3>
                  <p class="record-date">{{ record.healthRecordTime }}</p>
                </div>
              </div>
              <van-tag plain round :color="getRecordTypeColor(record.healthRecordType)" style="padding: 5px 10px;">
                {{ getRecordTypeName(record.healthRecordType) }}
              </van-tag>
            </div>
            
            <!-- 记录详情 -->
            <div class="record-content">
              <!-- 通用信息 -->
              <div class="info-row">
                <div class="info-item">
                  <font-awesome-icon icon="yen-sign" class="info-icon" />
                  <span class="info-label fixed-label">花费：</span>
                  <span class="info-value truncate">{{ record.healthRecordCost ? `¥${record.healthRecordCost}` : '无' }}</span>
                </div>
                <div class="info-item">
                  <font-awesome-icon icon="hospital" class="info-icon" />
                  <span class="info-label fixed-label">机构：</span>
                  <span class="info-value truncate">{{ record.hospitalName || '无' }}</span>
                </div>
              </div>
              
              <!-- 记录类型特有信息 -->
              <!-- 疫苗记录 -->
              <div v-if="record.healthRecordType === 1" class="info-row">
                <div class="info-item">
                  <font-awesome-icon icon="syringe" class="info-icon text-blue-400" />
                  <span class="info-label fixed-label">疫苗：</span>
                  <span class="info-value truncate">{{ record.vaccineName }}</span>
                </div>
                <div class="info-item">
                  <font-awesome-icon icon="calendar-alt" class="info-icon text-orange-400" />
                  <span class="info-label fixed-label">下次：</span>
                  <span class="info-value truncate">{{ record.vaccineNextTime }}</span>
                </div>
              </div>
              
              <!-- 驱虫记录 -->
              <div v-if="record.healthRecordType === 2" class="info-row">
                <div class="info-item">
                  <font-awesome-icon icon="bug" class="info-icon text-teal-400" />
                  <span class="info-label fixed-label">类型：</span>
                  <span class="info-value truncate">{{ record.healthRecordType }}</span>
                </div>
                <div class="info-item">
                  <font-awesome-icon icon="calendar-alt" class="info-icon text-orange-400" />
                  <span class="info-label fixed-label">下次：</span>
                  <span class="info-value truncate">{{ record.dewormingNextTime }}</span>
                </div>
              </div>
              
              <!-- 体检记录 -->
              <div v-if="record.healthRecordType === 3" class="info-row">
                <div class="info-item">
                  <font-awesome-icon icon="weight" class="info-icon text-purple-400" />
                  <span class="info-label fixed-label">体重：</span>
                  <span class="info-value truncate">{{ record.weight ? `${record.weight}kg` : '无' }}</span>
                </div>
                <div class="info-item">
                  <font-awesome-icon icon="thermometer-half" class="info-icon text-red-400" />
                  <span class="info-label fixed-label">体温：</span>
                  <span class="info-value truncate">{{ record.temperature ? `${record.temperature}℃` : '无' }}</span>
                </div>
              </div>
              
              <!-- 生病记录 -->
              <div v-if="record.healthRecordType === 4" class="info-row">
                <div class="info-item">
                  <font-awesome-icon icon="stethoscope" class="info-icon text-red-400" />
                  <span class="info-label fixed-label">症状：</span>
                  <span class="info-value truncate">{{ truncateText(record.symptom, 12) }}</span>
                </div>
                <div class="info-item">
                  <font-awesome-icon icon="notes-medical" class="info-icon text-blue-400" />
                  <span class="info-label fixed-label">诊断：</span>
                  <span class="info-value truncate">{{ truncateText(record.diagnosticResults, 12) || '无' }}</span>
                </div>
              </div>
              
              <!-- 附件预览 -->
              <div v-if="record.diagnosticImages && record.diagnosticImages.length > 0" class="attachments-container">
                <div class="attachments-label">
                  <font-awesome-icon icon="paperclip" class="mr-1" />
                  附件 ({{ record.diagnosticImages.split(',').length }})
                </div>
                <div class="attachments-grid">
                  <div 
                    v-for="(attachment, i) in record.diagnosticImages.split(',')" 
                    :key="i" 
                    class="attachment-item"
                    @click="previewImage(record.diagnosticImages,attachment)"
                  >
                    <img :src="attachment" class="attachment-image" />
                    <div class="attachment-overlay">
                      <font-awesome-icon icon="search" class="text-white" />
                    </div>
                  </div>
                </div>
              </div>
              <!-- 附件预览 -->
              <div v-if="record.inspectionImages && record.inspectionImages.length > 0" class="attachments-container">
                <div class="attachments-label">
                  <font-awesome-icon icon="paperclip" class="mr-1" />
                  附件 ({{ record.inspectionImages.split(',').length }})
                </div>
                <div class="attachments-grid">
                  <div 
                    v-for="(attachment, i) in record.inspectionImages.split(',')" 
                    :key="i" 
                    class="attachment-item"
                    @click="previewImage(record.inspectionImages,attachment)"
                  >
                    <img :src="attachment" class="attachment-image" />
                    <div class="attachment-overlay">
                      <font-awesome-icon icon="search" class="text-white" />
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 操作按钮 -->
              <div class="action-buttons">
                <van-button plain round size="small" @click="viewRecordDetail(record.healthRecordId)" class="detail-btn">
                  <font-awesome-icon icon="eye" class="detail-icon mr-1" />
                  查看详情
                </van-button>
                <van-button plain round type="danger" size="small" icon="delete-o" @click="deleteRecord(record.healthRecordId)">
                  删除
                </van-button>
              </div>
            </div>
          </div>
          
          <!-- 加载更多按钮 -->
          <div v-if="hasMoreRecords" class="load-more">
            <van-button plain round type="primary" :loading="loadingMore" @click="loadMoreRecords" class="theme-btn-outline">
              {{ loadingMore ? '加载中...' : '加载更多' }}
            </van-button>
          </div>
        </div>
      </div>
      
      <!-- 悬浮添加按钮 -->
      <div class="fixed-add-btn">
        <van-button 
          round 
          type="primary" 
          size="normal" 
          icon="plus"
          @click="goToAddRecord"
          class="shadow-xl theme-btn-primary"
        />
      </div>
    </div>
    

    
    <!-- 日期选择器 -->
    <van-popup v-model="showSingleDatePicker" position="bottom" round>
      <van-datetime-picker
        type="date"
        :min-date="new Date(2000, 0, 1)"
        :max-date="new Date()"
        :value="singleDatePickerValue"
        @confirm="onSingleDateConfirm"
        @cancel="showSingleDatePicker = false"
        title="选择日期"
        :show-toolbar="true"
      />
    </van-popup>
    
    <!-- 记录类型选择器 -->
    <van-popup v-model="showTypeSelector" position="bottom" round>
      <van-picker
        :columns="recordTypeOptions"
        @confirm="onTypeConfirm"
        @cancel="showTypeSelector = false"
        show-toolbar
        title="选择记录类型"
      />
    </van-popup>
    
    <!-- 图片预览 -->
    <van-image-preview 
      v-model="showImagePreview"
      :images="previewImages"
      :start-position="previewIndex"
    />
    
    <!-- 确认删除弹窗 -->
    <van-dialog
      v-model="showDeleteConfirm"
      title="确认删除"
      message="删除后无法恢复，确定要删除这条记录吗？"
      show-cancel-button
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
/**
 * 宠物健康记录查询页面
 * 支持按时间和记录类型筛选查询历史记录
 * @component
 */
import { RPC } from '../api/rpc'
import { mapGetters,mapActions } from 'vuex'
export default {
  name: 'HealthRecordList',
  data() {
    return {
      /**
       * 筛选条件
       * @type {Object}
       */
      filters: {
        startDate: '',
        endDate: '',
        recordType: '',
        petId: '',
        singleDate: '' // 新增的单一日期筛选
      },
      /**
       * 记录类型选择器显示状态
       * @type {Boolean}
       */
      showTypeSelector: false,
      /**
       * 记录类型选项
       * @type {Array}
       */
      recordTypeOptions: [
        { text: '全部类型', value: '' },
        { text: '疫苗', value:  1 },
        { text: '驱虫', value: 2 },
        { text: '体检', value: 3 },
        { text: '生病', value: 4 }
      ],
      /**
       * 健康记录列表
       * @type {Array}
       */
      records: [],
      /**
       * 是否正在加载
       * @type {Boolean}
       */
      loading: true,
      /**
       * 是否正在加载更多
       * @type {Boolean}
       */
      loadingMore: false,
      /**
       * 是否有更多记录
       * @type {Boolean}
       */
      hasMoreRecords: false,
      /**
       * 当前页码
       * @type {Number}
       */
      currentPage: 1,
      /**
       * 每页记录数
       * @type {Number}
       */
      pageSize: 10,
      /**
       * 单一日期选择器是否显示
       * @type {Boolean}
       */
      showSingleDatePicker: false,
      /**
       * 单一日期选择器值
       * @type {Date}
       */
      singleDatePickerValue: null,
      /**
       * 图片预览是否显示
       * @type {Boolean}
       */
      showImagePreview: false,
      /**
       * 预览图片列表
       * @type {Array}
       */
      previewImages: [],
      /**
       * 当前预览图片索引
       * @type {Number}
       */
      previewIndex: 0,
      /**
       * 删除确认对话框是否显示
       * @type {Boolean}
       */
      showDeleteConfirm: false,
      /**
       * 当前要删除的记录ID
       * @type {String}
       */
      currentDeleteId: null,

      /**
       * 记录总数
       * @type {Number}
       */
      totalRecords: 0,
      machineId: '',
      userId: '',
      devices: [],
    }
  },
  computed: {
    ...mapGetters(['backMachineId']),
    /**
     * 是否有激活的筛选条件
     * @returns {Boolean}
     */
    hasActiveFilters() {
      return this.filters.singleDate || 
             this.filters.recordType !== '';
    }
  },
  created() {
    this.userId = sessionStorage.getItem('info')?JSON.parse(sessionStorage.getItem('info')).userId : null
    console.log('11',this.backMachineId)
    // if (this.backMachineId) {
    //   this.machineId = this.backMachineId
    // }
    // 加载记录数据
    this.getDeviceList()
  },
  beforeDestroy() {
    this.setBackMachineId('')
  },
  methods: {
    ...mapActions(['setBackMachineId']),
    getDeviceList() {
      let data = {
        "pageIndex": 1,
        "pageSize": 100,
        "userId": this.userId
      }
      RPC.deviceList(data).then(res=>{
        if (res.list) {
          this.devices = res.list  
          res.list.forEach(item=>{
            if(item.isDefault === 1){
              this.machineId = this.backMachineId?this.backMachineId:item.machineId
              this.filters.petId = item.machineId
            }
          })
          // this.machineId = this.backMachineId?this.backMachineId:res.list[0].machineId
          // this.filters.petId = res.list[0].machineId
        
          this.fetchRecords();
        }
      })
    },
    /**
     * 格式化日期
     * @param {Date} date - 日期对象
     * @returns {String} 格式化后的日期字符串 YYYY-MM-DD
     */
    formatDate(date) {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    /**
     * 获取日期范围文本
     * @returns {String} 日期范围文本
     */
    getDateRangeText() {
      if (this.filters.startDate && this.filters.endDate) {
        return `${this.filters.startDate} 至 ${this.filters.endDate}`;
      } else if (this.filters.startDate) {
        return `${this.filters.startDate} 起`;
      } else if (this.filters.endDate) {
        return `至 ${this.filters.endDate}`;
      }
      return '';
    },
    /**
     * 获取记录类型图标
     * @param {String} type - 记录类型
     * @returns {String} 对应的图标名称
     */
    getRecordTypeIcon(type) {
      const icons = {
        1: 'syringe',
        2: 'bug',
        3: 'stethoscope',
        4: 'briefcase-medical'
      };
      return icons[type] || 'clipboard';
    },
    /**
     * 获取记录类型名称
     * @param {String} type - 记录类型
     * @returns {String} 中文名称
     */
    getRecordTypeName(type) {
      // const names = {
      //   vaccine: '疫苗',
      //   deworm: '驱虫',
      //   checkup: '体检',
      //   illness: '生病'
      // };
      const names = {
        1: '疫苗',
        2: '驱虫',
        3: '体检',
        4: '生病'
      };
      return names[type] || '未知';
    },
    /**
     * 获取记录类型颜色
     * @param {String} type - 记录类型
     * @returns {String} 颜色代码
     */
    getRecordTypeColor(type) {
      const colors = {
        1: '#4facfe',
        2: '#2dd4bf', // 更换为蓝绿色，替代荧光绿
        3: '#f6d365',
        4: '#ff7d89'
      };
      return colors[type] || '#4facfe';
    },
    /**
     * 获取记录类型样式类
     * @param {String} type - 记录类型
     * @returns {String} CSS类名
     */
    getRecordTypeClass(type) {
      const classes = {
        1: 'bg-gradient-to-r from-blue-400 to-blue-500',
        2: 'bg-gradient-to-r from-teal-400 to-teal-500', // 更换为蓝绿色渐变
        3: 'bg-gradient-to-r from-yellow-400 to-yellow-500',
        4: 'bg-gradient-to-r from-red-400 to-red-500'
      };
      return classes[type] || 'bg-gradient-to-r from-blue-400 to-blue-500';
    },
    /**
     * 获取图标颜色类
     * @param {String} type - 记录类型
     * @returns {String} CSS类名
     */
    getIconColorClass(type) {
      const classes = {
        1: 'text-blue-500',
        2: 'text-teal-500',
        3: 'text-yellow-500',
        4: 'text-red-500'
      };
      return classes[type] || 'text-blue-500';
    },
    /**
     * 选择筛选标签
     * @param {String} type - 记录类型
     */
    selectFilterTab(type) {
      this.filters.recordType = type;
      this.searchRecords();
    },
    
    /**
     * 获取选中的记录类型文本
     * @returns {String} 记录类型文本
     */
    getSelectedTypeText() {
      const selectedType = this.recordTypeOptions.find(option => option.value === this.filters.recordType);
      return selectedType ? selectedType.text : '全部类型';
    },
    
    /**
     * 记录类型确认处理
     * @param {Object} value - 选择的记录类型对象
     */
    onTypeConfirm(value) {
      this.filters.recordType = value.value;
      this.showTypeSelector = false;
    },
    /**
     * 开始日期确认处理
     * @param {Date} value - 选择的日期
     */
    onSingleDateConfirm(value) {
      this.singleDatePickerValue = value;
      this.filters.singleDate = this.formatDate(value);
      this.currentPage = 1;
      this.records = [];
      this.fetchRecords()
      this.showSingleDatePicker = false;
    },

    /**
     * 清除日期筛选
     */
    clearDateFilter() {
      this.filters.singleDate = '';
      this.singleDatePickerValue = null;
      this.searchRecords();
    },
    /**
     * 选择宠物
     * @param {String} petId - 宠物ID
     */
    selectPet(petId) {
      if (this.machineId === petId) return;
      
      this.machineId = petId;
      this.filters.petId = petId;
      this.currentPage = 1;
      this.records = [];
      this.fetchRecords();
    },
    
    /**
     * 搜索记录
     */
    searchRecords() {
      this.currentPage = 1;
      this.records = [];
      this.fetchRecords();
    },
    /**
     * 获取记录数据
     */
    async fetchRecords() {
      this.loading = true;
      try {
        let data = {
          "healthRecordTime": this.filters.singleDate,
          "healthRecordType": this.filters.recordType,
          "machineId": this.machineId,
          "pageIndex": this.currentPage,
          "pageSize": this.pageSize
        }
        RPC.healthRecordList(data).then(res=>{
          this.totalRecords = res.count
          this.records = [...this.records, ...res.list];
          this.hasMoreRecords = res.count > this.currentPage * this.pageSize;
        })
      } catch (error) {
        console.error('获取健康记录失败:', error);
        this.$toast.fail('获取记录失败，请重试');
      } finally {
        this.loading = false;
      }
    },
    /**
     * 加载更多记录
     */
    loadMoreRecords() {
      this.loadingMore = true;
      this.currentPage += 1;
      
      this.fetchRecords().finally(() => {
        this.loadingMore = false;
      });
    },
    /**
     * 跳转到添加记录页面
     */
    goToAddRecord() {
      this.$router.push({
        path: '/health-record',
        query: { petId: this.filters.petId }
      });
    },
    /**
     * 查看记录详情
     * @param {String} id - 记录ID
     */
    viewRecordDetail(id) {
      this.$router.push({
        path: '/health-record-detail',
        query: { id }
      });
    },
    /**
     * 删除记录
     * @param {String} id - 记录ID
     */
    deleteRecord(id) {
      this.currentDeleteId = id;
      this.showDeleteConfirm = true;
    },
    /**
     * 确认删除记录
     */
    async confirmDelete() {
      try {
        
        RPC.healthRecordDelete({healthRecordId:this.currentDeleteId}).then(res=>{
          this.$toast.success('删除成功');
          this.currentPage = 1;
          this.records = [];
          this.fetchRecords();
        })
        
      } catch (error) {
        console.error('删除记录失败:', error);
        this.$toast.fail('删除失败，请重试');
      }
    },
    /**
     * 预览图片
     * @param {String} url - 图片URL
     */
    previewImage(ary,url) {
      // 获取当前记录的所有图片
      // const record = this.records.find(r => 
      //   r.attachments.some(a => a.url === url)
      // );
      
      // if (record) {
        this.previewImages = ary.split(',');
        this.previewIndex = this.previewImages.indexOf(url);
        this.showImagePreview = true;
      // }
    },
    /**
     * 截断文本，超出部分显示省略号
     * @param {String} text - 要截断的文本
     * @param {Number} maxLength - 最大长度
     * @returns {String} 截断后的文本
     */
    truncateText(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength) + '...';
    }
  }
}
</script>

<style scoped>
.theme-header {
  background-image: var(--gradient-header);
  width: 100%;
  z-index: 50;
}

.fixed-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  margin-bottom: 20px; /* 增加与内容的间距 */
}

.pet-partners-container {
  padding: 12px 16px 8px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #f0f0f0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.pet-partners-scroll {
  display: flex;
  padding-bottom: 4px;
}

.pet-partner-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 16px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.pet-partner-item:last-child {
  margin-right: 0;
}

.pet-partner-item:active {
  transform: scale(0.95);
}

.pet-avatar-container {
  position: relative;
  margin-bottom: 4px;
}

.pet-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e0e0e0;
  transition: all 0.3s ease;
}

.pet-partner-active .pet-avatar {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
}

.pet-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #ccc;
  border: 2px solid white;
}

.status-online {
  background-color: #10b981;
}

.pet-name {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  max-width: 60px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pet-partner-active .pet-name {
  color: var(--primary-color);
  font-weight: 500;
}

.query-container {
  padding: 12px 0 0;
  background-color: white;
  border-bottom: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px; /* 增加底部间距 */
}

.filter-tabs {
  display: flex;
  overflow-x: auto;
  padding: 0 12px 12px; /* 增加底部内边距 */
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  border-bottom: 1px solid #f0f0f0;
}

.filter-tab {
  flex-shrink: 0;
  padding: 8px 12px;
  margin-right: 8px;
  border-radius: 16px;
  font-size: 13px;
  color: #64748b;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.filter-tab:last-child {
  margin-right: 0;
}

.filter-tab-active {
  color: var(--primary-color);
  background-color: #eff6ff;
  border-color: #bfdbfe;
  font-weight: 500;
}

.filter-tab-icon {
  margin-right: 6px;
  font-size: 12px;
}

.date-filter {
  display: flex;
  align-items: center;
  padding: 10px 16px 15px; /* 增加底部内边距 */
  border-bottom: 1px solid #f0f0f0;
}

.date-filter-input {
  display: flex;
  align-items: center;
  flex: 1;
  cursor: pointer;
}

.date-filter-icon {
  color: var(--primary-color);
  margin-right: 8px;
  font-size: 14px;
}

.date-value {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.date-placeholder {
  font-size: 14px;
  color: #94a3b8;
}

.date-clear-btn {
  color: #cbd5e1;
  font-size: 16px;
  padding: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
}

.date-clear-btn:hover {
  color: #94a3b8;
}

.content-container {
  padding-top: 250px; /* 大幅增加顶部内边距，确保卡片列表不被条件筛选挡住 */
  padding-bottom: 80px;
  padding-left: 16px;
  padding-right: 16px;
}

/* 移除旧的样式 */

.filter-tag {
  height: 28px;
  line-height: 26px;
  font-size: 12px;
  white-space: nowrap;
}

.records-summary {
  font-size: 13px;
  color: #666;
  margin-bottom: 16px;
  padding-left: 4px;
  margin-top: 8px;
}

.top-spacer {
  height: 0px; /* 不需要额外的顶部间距 */
}

.extra-spacer {
  height: 20px; /* 额外的顶部间距，确保卡片不被筛选栏遮挡 */
}

.top-spacer-empty {
  height: 10px; /* 空状态下的额外顶部间距 */
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}



.date-field :deep(.van-field__label) {
  width: 5em;
}

.van-dropdown-menu {
  box-shadow: none;
  height: 20px;
}

:deep(.van-button--primary) {
  background: linear-gradient(to right, var(--primary-color), var(--primary-light));
  border: none;
}

.fixed-add-btn :deep(.van-button--primary) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

:deep(.van-nav-bar .van-icon) {
  color: var(--text-primary);
}

:deep(.van-nav-bar__title) {
  color: var(--text-primary);
  font-weight: 600;
}

::-webkit-scrollbar {
  display: none;
}

.record-card {
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
  overflow: hidden;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  position: relative;
  z-index: 1;
}

.fixed-add-btn {
  position: fixed;
  right: 20px;
  bottom: 96px;
  z-index: 200; /* 确保按钮在最顶层 */
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.record-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 16px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.record-header.bg-gradient-to-r.from-blue-400.to-blue-500 {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-light) 100%);
}

.record-header.bg-gradient-to-r.from-teal-400.to-teal-500 {
  background: linear-gradient(135deg, #2dd4bf 0%, #14b8a6 100%);
}

.record-header.bg-gradient-to-r.from-yellow-400.to-yellow-500 {
  background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
}

.record-header.bg-gradient-to-r.from-red-400.to-red-500 {
  background: linear-gradient(135deg, #ff7d89 0%, #ff7d89 100%);
}

.record-icon-container {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 12px;
}

.record-title {
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
  line-height: 1.2;
}

.record-date {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.record-content {
  padding: 16px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
  width: 48%;
}

.info-icon {
  font-size: 16px;
  margin-right: 8px;
  color: var(--primary-color);
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.info-label {
  font-weight: 500;
  color: #555;
  margin-right: 8px;
}

.fixed-label {
  width: 42px;
  flex-shrink: 0;
}

.info-value {
  font-weight: 600;
  color: var(--primary-color);
  flex-grow: 1;
  overflow: hidden;
}



.attachments-container {
  margin-top: 16px;
  margin-bottom: 16px;
}

.attachments-label {
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
}

.attachment-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f5f5;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.attachment-item:hover {
  opacity: 0.9;
}

.attachment-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.attachment-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.attachment-item:hover .attachment-overlay {
  opacity: 1;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
}

.detail-btn {
  border-color: #3b82f6;
  color: #3b82f6;
  font-weight: 600;
}

.detail-btn:active {
  background-color: #3b82f6;
  color: white;
}

.detail-icon {
  color: #3b82f6;
  font-size: 14px;
}

.load-more {
  text-align: center;
  padding: 16px 0;
}

.full-width {
  width: 100%;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 100%;
}
</style> 